import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";

// Material-UI Icons
import HomeIcon from "@mui/icons-material/Home";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import AssessmentIcon from "@mui/icons-material/Assessment";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SettingsIcon from "@mui/icons-material/Settings";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import ShareIcon from "@mui/icons-material/Share";
import PeopleIcon from "@mui/icons-material/People";
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import InstagramIcon from "@mui/icons-material/Instagram";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LanguageIcon from "@mui/icons-material/Language";
import InsightsIcon from "@mui/icons-material/Insights";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import HistoryIcon from "@mui/icons-material/History";
import StorageIcon from "@mui/icons-material/Storage";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CloseIcon from "@mui/icons-material/Close";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import NotificationsIcon from "@mui/icons-material/Notifications";

interface SidebarProps {
  className?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

interface NavSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  items: NavItem[];
  defaultExpanded?: boolean;
}

interface NavItem {
  title: string;
  path: string;
  icon: React.ReactNode;
  description: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  className,
  isOpen = false,
  onClose,
}) => {
  const location = useLocation();
  const { hasPageAccess } = useAuth();

  // Load collapse state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebar_collapsed");
    return saved === "true";
  });

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (onClose && isOpen) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Handle escape key to close sidebar on mobile
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, isOpen]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Load expanded sections from localStorage
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    const saved = localStorage.getItem("sidebar_expanded_sections");
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        return new Set(["social", "ai-usage", "knowledge", "system"]);
      }
    }
    return new Set(["social", "ai-usage", "knowledge", "system"]);
  });

  // Save collapse state
  useEffect(() => {
    localStorage.setItem("sidebar_collapsed", String(isCollapsed));
  }, [isCollapsed]);

  // Save expanded sections
  useEffect(() => {
    localStorage.setItem(
      "sidebar_expanded_sections",
      JSON.stringify(Array.from(expandedSections))
    );
  }, [expandedSections]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  // Navigation structure (memoized to prevent unnecessary re-renders)
  const sections: NavSection[] = useMemo(
    () => [
      {
        id: "social",
        title: "Social",
        icon: <ShareIcon />,
        items: [
          {
            title: "Accounts Hub",
            path: "/social/accounts",
            icon: <PeopleIcon />,
            description:
              "Manage social accounts, sync posts, and chat with AI personas",
          },
          {
            title: "Feed",
            path: "/social/feed",
            icon: <DynamicFeedIcon />,
            description: "Browse posts from Here4Good and configured accounts",
          },
          {
            title: "Analytics",
            path: "/social/analytics",
            icon: <TrendingUpIcon />,
            description: "Social media performance dashboard for Here4Good",
          },
          {
            title: "Instagram",
            path: "/social/instagram",
            icon: <InstagramIcon />,
            description: "Connect and manage Instagram business accounts",
          },
          {
            title: "Feed Analysis",
            path: "/social/feed-analysis",
            icon: <InsightsIcon />,
            description: "AI-powered social media feed analysis",
          },
        ],
      },
      {
        id: "ai-usage",
        title: "AI Usage",
        icon: <BarChartIcon />,
        items: [
          {
            title: "Overview",
            path: "/ai-usage/overview",
            icon: <AssessmentIcon />,
            description: "System-wide analytics and usage statistics",
          },
          {
            title: "Messages",
            path: "/ai-usage/messages",
            icon: <ChatBubbleOutlineIcon />,
            description: "View all user conversations and AI responses",
          },
          {
            title: "Cost Analytics",
            path: "/ai-usage/costs",
            icon: <AttachMoneyIcon />,
            description: "API cost tracking and projections",
          },
        ],
      },
      {
        id: "knowledge",
        title: "Knowledge Base",
        icon: <MenuBookIcon />,
        items: [
          {
            title: "Document Library",
            path: "/knowledge/documents",
            icon: <LibraryBooksIcon />,
            description: "Browse and manage indexed documents",
          },
          {
            title: "Data Sources",
            path: "/knowledge/sources",
            icon: <FolderOpenIcon />,
            description: "Documents and X accounts for RAG",
          },
          {
            title: "Indexing Jobs",
            path: "/knowledge/indexing",
            icon: <CloudSyncIcon />,
            description: "Monitor document processing and indexing",
          },
          {
            title: "Scheduled Jobs",
            path: "/knowledge/scheduled",
            icon: <ScheduleIcon />,
            description: "Manage automated recurring tasks",
          },
          {
            title: "Backfill Jobs",
            path: "/knowledge/backfill",
            icon: <HistoryIcon />,
            description: "Manage async X account backfill operations",
          },
        ],
      },
      {
        id: "system",
        title: "System",
        icon: <SettingsIcon />,
        items: [
          {
            title: "User Management",
            path: "/system/users",
            icon: <PeopleIcon />,
            description: "Manage user labels, categories, and auto-labeling rules",
          },
          {
            title: "Notifications",
            path: "/system/notifications",
            icon: <NotificationsIcon />,
            description: "Configure email notifications and alerts",
          },
          {
            title: "Website Whitelist",
            path: "/system/whitelist",
            icon: <LanguageIcon />,
            description: "Manage trusted domains for web search",
          },
          {
            title: "Search Debugger",
            path: "/system/search",
            icon: <SearchIcon />,
            description: "Test and debug RAG search queries",
          },
          {
            title: "DB Inspector",
            path: "/system/db",
            icon: <StorageIcon />,
            description: "Inspect Firestore collections and documents",
          },
        ],
      },
      {
        id: "admin",
        title: "Admin",
        icon: <AdminPanelSettingsIcon />,
        items: [
          {
            title: "Admin Dashboard",
            path: "/admin",
            icon: <AdminPanelSettingsIcon />,
            description: "Manage users and role permissions",
          },
        ],
      },
    ],
    []
  );

  // Filter sections and items based on user permissions
  const filteredSections = useMemo(() => {
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => hasPageAccess(item.path)),
      }))
      .filter((section) => section.items.length > 0);
  }, [sections, hasPageAccess]);

  const isActive = useCallback(
    (path: string) => {
      if (path === "/") return location.pathname === "/";

      // Exact match
      if (location.pathname === path) return true;

      // Get all explicitly defined paths from navigation sections
      const allDefinedPaths = filteredSections.flatMap((s) =>
        s.items.map((i) => i.path)
      );

      // If current location exactly matches a defined route,
      // don't activate parent paths (prevents /social from activating when on /social/sources)
      if (allDefinedPaths.includes(location.pathname)) {
        return false;
      }

      // Allow prefix match for dynamic routes (e.g., /jobs/123 matches /jobs)
      return location.pathname.startsWith(path + "/");
    },
    [location.pathname, filteredSections]
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => onClose?.()}
          aria-hidden="true"
        />
      )}

      <aside
        className={`sidebar ${isCollapsed ? "collapsed" : ""} ${
          isOpen ? "mobile-open" : ""
        } ${className || ""}`}
        aria-label="Main navigation"
      >
        {/* Sidebar Header */}
        <div className="sidebar-header">
          {!isCollapsed && (
            <div className="sidebar-brand">
              <h2 className="sidebar-title">
                BrightMind <span className="title-accent">AI</span>
              </h2>
              <span className="sidebar-app-name">Back Office</span>
            </div>
          )}

          {/* Mobile close button */}
          <button
            className="sidebar-close-mobile hide-desktop"
            onClick={() => onClose?.()}
            aria-label="Close navigation menu"
          >
            <CloseIcon />
          </button>

          {/* Desktop collapse toggle */}
          <button
            className="sidebar-toggle hide-mobile"
            onClick={toggleCollapse}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {/* Dashboard Link */}
          {hasPageAccess("/") && (
            <Tooltip
              title={isCollapsed ? "Dashboard" : ""}
              placement="right"
              arrow
            >
              <Link
                to="/"
                className={`sidebar-item sidebar-item-single ${
                  location.pathname === "/" ? "active" : ""
                }`}
              >
                <HomeIcon className="sidebar-icon" />
                {!isCollapsed && (
                  <span className="sidebar-label">Dashboard</span>
                )}
              </Link>
            </Tooltip>
          )}

          {hasPageAccess("/") && <div className="sidebar-divider" />}

          {/* AI Chat - Promoted */}
          {hasPageAccess("/chat") && (
            <Tooltip
              title={isCollapsed ? "AI Chat" : "Chat with AI assistant"}
              placement="right"
              arrow
            >
              <Link
                to="/chat"
                className={`sidebar-item sidebar-item-single sidebar-item-promoted ${
                  isActive("/chat") ? "active" : ""
                }`}
              >
                <ChatBubbleOutlineIcon className="sidebar-icon" />
                {!isCollapsed && <span className="sidebar-label">AI Chat</span>}
              </Link>
            </Tooltip>
          )}

          {hasPageAccess("/chat") && <div className="sidebar-divider" />}

          {/* Sections */}
          {filteredSections.map((section) => {
            const isExpanded = expandedSections.has(section.id);
            const hasActiveItem = section.items.some((item) =>
              isActive(item.path)
            );

            return (
              <div key={section.id} className="sidebar-section">
                {/* Section Header */}
                <Tooltip
                  title={
                    isCollapsed
                      ? section.title
                      : section.items.map((item) => item.title).join(", ")
                  }
                  placement="right"
                  arrow
                >
                  <button
                    className={`sidebar-section-header ${
                      hasActiveItem ? "active" : ""
                    }`}
                    onClick={() => toggleSection(section.id)}
                  >
                    <div className="sidebar-section-title">
                      <span className="sidebar-icon">{section.icon}</span>
                      {!isCollapsed && (
                        <span className="sidebar-label">{section.title}</span>
                      )}
                    </div>
                    {!isCollapsed && (
                      <ExpandMoreIcon
                        className={`sidebar-section-arrow ${
                          isExpanded ? "expanded" : ""
                        }`}
                      />
                    )}
                  </button>
                </Tooltip>

                {/* Section Items */}
                {!isCollapsed && isExpanded && (
                  <div className="sidebar-section-items">
                    {section.items.map((item) => (
                      <Tooltip
                        key={item.path}
                        title={item.description}
                        placement="right"
                        arrow
                      >
                        <Link
                          to={item.path}
                          className={`sidebar-item sidebar-item-nested ${
                            isActive(item.path) ? "active" : ""
                          }`}
                        >
                          <span className="sidebar-icon">{item.icon}</span>
                          <span className="sidebar-label">{item.title}</span>
                        </Link>
                      </Tooltip>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
